import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  IconButton,
  SelectChangeEvent,
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/useApi';

interface PortData {
  id?: number;
  portNumber: string;
  maxOnus: number;
  txpower?: number;
  status: 'active' | 'inactive' | 'maintenance';
  description?: string;
}

interface OlTCardPortFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  oltId: string;
  cardSlot: number;
  initialData?: PortData | null;
}

const OlTCardPortForm: React.FC<OlTCardPortFormProps> = ({
  open,
  onClose,
  onSuccess,
  oltId,
  cardSlot,
  initialData,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    portNumber: '',
    maxOnus: '64',
    txPower: '5.0',
    status: 'active' as 'active' | 'inactive' | 'maintenance',
    description: '',
  });

  useEffect(() => {
    if (open) {
      if (initialData) {
        // txpower: normalizar para 2 casas decimais com ponto (ex: 5.130 -> 5.13)
        const txPowerVal = initialData.txpower != null
          ? Number(initialData.txpower).toFixed(2)
          : '0';
        setFormData({
          portNumber: initialData.portNumber,
          maxOnus: initialData.maxOnus.toString(),
          txPower: txPowerVal,
          status: initialData.status,
          description: initialData.description || '',
        });
      } else {
        setFormData({
          portNumber: '',
          maxOnus: '64',
          txPower: '5.0',
          status: 'active',
          description: '',
        });
      }
      setError(null);
    }
  }, [open, initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const name = e.target.name as keyof typeof formData;
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleStatusChange = (e: SelectChangeEvent<'active' | 'inactive' | 'maintenance'>) => {
    setFormData((prev) => ({ ...prev, status: e.target.value as 'active' | 'inactive' | 'maintenance' }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const portNumber = formData.portNumber.trim();
    const maxOnus = parseInt(formData.maxOnus, 10);
    // Aceitar tanto vírgula quanto ponto como separador decimal (pt-BR vs en)
    const txPower = parseFloat(String(formData.txPower).replace(',', '.'));

    if (!portNumber) {
      setError('Número da porta é obrigatório');
      return;
    }

    if (isNaN(maxOnus) || maxOnus < 1 || maxOnus > 256) {
      setError('Máximo de ONUs deve ser entre 1 e 256');
      return;
    }

    if (isNaN(txPower) || txPower < -50 || txPower > 20) {
      setError('Potência de TX deve ser um número entre -50 e 20 dBm');
      return;
    }

    setLoading(true);
    try {
      let response;
      if (initialData && initialData.id) {
        response = await api.put(`/olts/${oltId}/cards/${cardSlot}/ports/${initialData.id}`, {
          portNumber,
          maxOnus,
          txPower,
          status: formData.status,
          description: formData.description || undefined,
        });
      } else {
        response = await api.post(`/olts/${oltId}/cards/${cardSlot}/ports`, {
          portNumber,
          maxOnus,
          txPower,
          status: formData.status,
          description: formData.description || undefined,
        });
      }

      if (response.data.success) {
        toast.success(initialData ? 'Porta atualizada com sucesso!' : 'Porta cadastrada com sucesso!');
        setFormData({
          portNumber: '',
          maxOnus: '64',
          status: 'active',
          txPower: '5.0',
          description: '',
        });
        onSuccess();
        onClose();
      } else {
        setError(response.data.message || (initialData ? 'Erro ao atualizar porta' : 'Erro ao cadastrar porta'));
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || (initialData ? 'Erro ao atualizar porta' : 'Erro ao cadastrar porta');
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      portNumber: '',
      maxOnus: '64',
      status: 'active',
      txPower: '5.0',
      description: '',
    });
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 1 } }}>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <AddIcon color="primary" />
            {initialData ? `Editar Porta ${initialData.portNumber} - Slot ${cardSlot}` : `Cadastrar Porta - Slot ${cardSlot}`}
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ p: 0.5 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Número da porta"
                name="portNumber"
                value={formData.portNumber}
                onChange={handleChange}
                required
                size="small"
                placeholder="Ex: 1, 2, 1/1/1"
                helperText="Identificador da porta PON"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Máximo de ONUs"
                name="maxOnus"
                value={formData.maxOnus}
                onChange={handleChange}
                type="number"
                inputProps={{ min: 1, max: 256 }}
                size="small"
                helperText="Capacidade máxima (1 a 256)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Potência de TX (dBm)"
                name="txPower"
                value={formData.txPower}
                onChange={handleChange}
                type="number"
                inputProps={{ min: -50, max: 20, step: 0.01 }}
                size="small"
                helperText="Potência de transmissão (dBm)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select name="status" value={formData.status} onChange={handleStatusChange} label="Status">
                  <MenuItem value="active">Ativa</MenuItem>
                  <MenuItem value="inactive">Inativa</MenuItem>
                  <MenuItem value="maintenance">Manutenção</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                name="description"
                value={formData.description}
                onChange={handleChange}
                size="small"
                placeholder="Ex: Porta PON principal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={handleClose} color="inherit" disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" color="primary" disabled={loading}>
            {loading ? (initialData ? 'Salvando...' : 'Cadastrando...') : (initialData ? 'Salvar' : 'Cadastrar')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default OlTCardPortForm;
